import React, { useEffect, useId, useRef, useState } from "react";

const sectionColors = {
    "Property Details": { bg: "bg-[#2E7D6B]", iconBg: "bg-white/20", iconText: "text-white" },
    Outreach: { bg: "bg-[#C8743A]", iconBg: "bg-white/20", iconText: "text-white" },
    "Development Plans": { bg: "bg-[#6B5FA7]", iconBg: "bg-white/20", iconText: "text-white" },
    "Economic Benefit": { bg: "bg-[#21506F]", iconBg: "bg-white/20", iconText: "text-white" },
    "Site Information": { bg: "bg-[#C8743A]", iconBg: "bg-white/20", iconText: "text-white" },
    Valuation: { bg: "bg-[#21506F]", iconBg: "bg-white/20", iconText: "text-white" },
    Metadata: { bg: "bg-[#6B5FA7]", iconBg: "bg-white/20", iconText: "text-white" },
    
} as const;

export function ParcelSection({
    title,
    icon,
    children,
    columns = 2,
    collapsible = false,
	defaultExpanded = true,
	isEditing = false,
	openWhenEditing = false,
	closeWhenEditing = false,
}: React.PropsWithChildren<{
    title: string;
    icon?: React.ReactNode;
    columns?: 1 | 2 | 3;
    collapsible?: boolean;
	defaultExpanded?: boolean;
	isEditing?: boolean;
	openWhenEditing?: boolean;
	closeWhenEditing?: boolean;
}>) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const contentId = useId();
	const wasEditingRef = useRef(isEditing);

    const gridCols = {
        1: "grid-cols-1",
        2: "grid-cols-1 sm:grid-cols-2",
        3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    };

    const colors = sectionColors[title as keyof typeof sectionColors] ?? {
        bg: "bg-[#21506F]",
        iconBg: "bg-white/20",
        iconText: "text-white",
    };

    useEffect(() => {
		const editingJustStarted = !wasEditingRef.current && isEditing;

		if (editingJustStarted) {
			if (closeWhenEditing) {
				setIsExpanded(false);
			} else if (openWhenEditing) {
				setIsExpanded(true);
			}
		}

		wasEditingRef.current = isEditing;
	}, [isEditing, openWhenEditing, closeWhenEditing]);

	const toggleSection = () => {
		if (!collapsible) return;
		setIsExpanded((prev) => !prev);
	};

    const headerContent = (
		<>
			<div className="flex items-center gap-2">
				{icon && React.isValidElement(icon) && (
					<div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colors.iconBg}`}>
						{React.cloneElement(icon as React.ReactElement, {
							className: `h-6 w-6 ${colors.iconText}`,
						})}
					</div>
				)}
				<h3 className="text-sm font-semibold uppercase text-white">
					{title}
				</h3>
			</div>

			{collapsible && (
				<svg
					className={`h-5 w-5 text-white transition-transform ${isExpanded ? "rotate-180" : ""}`}
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
					aria-hidden="true"
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
				</svg>
			)}
		</>
	);

    return (
		<section className="group relative overflow-hidden rounded-xl border border-border bg-white shadow-md hover:shadow-lg">
			{collapsible ? (
				<button
					type="button"
					onClick={toggleSection}
					aria-expanded={isExpanded}
					aria-controls={contentId}
					className={`flex w-full items-center justify-between gap-2 border-b border-border px-4 py-3 text-left ${colors.bg}`}
				>
					{headerContent}
				</button>
			) : (
				<header className={`flex items-center justify-between gap-2 border-b border-border px-4 py-3 ${colors.bg}`}>
					{headerContent}
				</header>
			)}

			{(!collapsible || isExpanded) && (
				<div id={contentId} className={`grid gap-x-6 gap-y-4 p-4 ${gridCols[columns]}`}>
					{children}
				</div>
			)}
		</section>
	);
}